//src/components/onboarding/ChatMessage.tsx
import { Camera, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

export function ChatMessage({
  message,
  isMobile,
  selectedIDType,
  currentStage,
  onOptionClick,
  onFileUpload,
  onStartCamera,
  setShowBankingForm,
}) {
  // Get document type for camera
  const getDocumentType = () => {
    if (message.attachmentType === "id") {
      return selectedIDType === "Passport"
        ? "passport"
        : selectedIDType === "Driving Licence"
          ? "driving-licence"
          : "id-card";
    }
    if (message.attachmentType === "selfie") return "selfie";
    if (message.attachmentType === "proof_of_address")
      return "proof_of_address";
    return null;
  };

  // Get attachment title
  const getAttachmentTitle = () => {
    const titles = {
      id: "Upload ID Document",
      selfie: "Upload Selfie Photo",
      proof_of_address: "Upload Proof of Address",
      funds_proof: "Upload Financial Document",
      gift_letter: "Upload Gift Letter",
    };
    return titles[message.attachmentType] || "Upload Document";
  };

  // Handle camera click
  const handleCameraClick = () => {
    const docType = getDocumentType();
    if (docType) {
      onStartCamera(docType);
    }
  };

  // Render upload section
  const renderUploadSection = () => (
    <div
      className={`mt-3 w-full overflow-hidden ${message.isDisabled ? "pointer-events-none opacity-50" : ""}`}
    >
      <div className="bg-white p-3 rounded-md shadow-sm border border-gray-100">
        <h3 className="text-sm font-medium text-gray-900 mb-1">
          {getAttachmentTitle()}
        </h3>

        <div
          className={`${isMobile ? "grid grid-cols-1" : "grid grid-cols-2"} gap-3 mb-3`}
        >
          <button
            type="button"
            onClick={handleCameraClick}
            className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <Camera className="h-7 w-7 text-primary mb-1" />
            <span className="text-sm font-medium text-primary">Take Photo</span>
            <span className="text-xs text-gray-500 mt-1">Use camera</span>
          </button>

          {!isMobile && (
            <div>
              <label
                htmlFor={`file-upload-${message.id}`}
                className="cursor-pointer"
              >
                <div className="flex flex-col items-center justify-center border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                  <Upload className="h-7 w-7 text-primary mb-1" />
                  <span className="text-sm font-medium text-primary">
                    Upload File
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    From device
                  </span>
                </div>
              </label>
              <input
                id={`file-upload-${message.id}`}
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,application/pdf"
                onChange={onFileUpload(message)}
              />
            </div>
          )}
        </div>

        <div className="text-xs text-gray-500 mt-3">
          <p>For best results:</p>
          <ul className="list-disc pl-4 space-y-0.5 mt-1">
            <li>Ensure the entire document is visible</li>
            <li>Make sure text is clear and readable</li>
            <li>Avoid glare and reflections</li>
          </ul>
        </div>
      </div>
    </div>
  );

  // Render option buttons
  const renderOptions = () => (
    <div className="mt-3 space-y-2">
      {message.options.map((option, idx) => (
        <Button
          key={idx}
          size="sm"
          disabled={message.isDisabled}
          variant="outline"
          className="mr-2 mb-2 bg-[#4c7375] text-white border-0 hover:bg-[#4c7375]/90 disabled:opacity-50"
          onClick={() => onOptionClick(option)}
        >
          {option}
        </Button>
      ))}
    </div>
  );

  // Render banking form button
  const renderBankingForm = () => (
    <div className="mt-3 w-full">
      <Button
        onClick={() => setShowBankingForm(true)}
        className="mr-2 mb-2 bg-[#4c7375] text-white border-0 hover:bg-[#4c7375]/90"
      >
        Enter Banking Details
      </Button>
    </div>
  );

  // Get message styling with dynamic colors
  const getMessageStyle = () => {
    const baseStyle = "max-w-[80%] rounded-xl p-3 border";
    const disabledStyle = message.isDisabled ? "opacity-60" : "";
    const isTipMessage = message.content && message.content.includes("Tip:");
    if (isTipMessage) {
      return `${baseStyle} bg-[#FEF4DF] text-[#304145] border-0 text-[12px] `;
    }

    if (message.isUser && !isTipMessage) {
      // Check if this is a suggested topic message
      const isSuggestedTopic =
        message.content &&
        (message.content.includes("Can I use my bus pass as ID?") ||
          message.content.includes(
            "I don't have a passport or driving licence",
          ) ||
          message.content.includes("My ID has expired") ||
          message.content.includes("What counts as proof of address?") ||
          message.content.includes(
            "Why am I being asked for Source of Funds?",
          ) ||
          message.content.includes(
            "I've been asked to upload a document again",
          ) ||
          message.content.includes(
            "What happens if I can't complete the check",
          ));

      if (isSuggestedTopic) {
        // Lavender for suggested topics
        return `${baseStyle} bg-[#D6CFDD] text-[#304145] border-0 text-[12px] `;
      } else {
        // Teal for regular user messages
        return `${baseStyle} bg-[#2D5758] text-[#daedeb] border-0 text-[12px] `;
      }
    } else {
      // Mint for bot messages
      return `${baseStyle} bg-[#DAEDEB] text-[#243d3d] border-0 text-[12px] `;
    }
  };

  // Get border radius style
  const getBorderRadiusStyle = () => {
    if (message.isUser) {
      return { borderBottomRightRadius: "0" };
    }
    return { borderBottomLeftRadius: "0" };
  };

  const formatContent = (content) => {
    // Ensure line breaks are preserved
    return content.replace(/\n/g, "<br/>").replace(/(\d+)\.\s/g, "<br/>$1. "); // Add line break before numbered items
  };

  const renderMessageContent = () => {
    return (
      <>
        {/* Message content - consider sanitizing HTML */}
        <div
          dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
        />

        {/* Upload section */}
        {message.attachmentType &&
          message.actionType === "upload" &&
          renderUploadSection()}

        {/* Option buttons */}
        {message.options && renderOptions()}

        {/* Banking form */}
        {message.actionType === "form" &&
          message.formId &&
          currentStage === "proof_of_funds" &&
          !message.isDisabled &&
          renderBankingForm()}
      </>
    );
  };

  return (
    <div className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}>
      <div className={getMessageStyle()} style={getBorderRadiusStyle()}>
        {renderMessageContent()}
      </div>
    </div>
  );
}
